<template>
  <div id="app">
      <van-popup v-model="$store.state.showLoading" :close-on-click-overlay="false" style="padding:20px;width:60%;">
        <van-loading size="24px" color="#E8543A">{{ $t('loading') }}</van-loading>
      </van-popup>

      <van-popup v-model="$store.state.needLogin" round position="center" class="login-popup"
        style="width:85%;max-width:480px;padding: 20px 10px;" closeable close-icon="cross"
        @opened="onLoginPopupOpened">
        <van-tabs v-model="loginDialog.activeTab" type="none" shrink class="login-tabs">
          <van-tab name="login">
            <template #title>
              <div class="custom-title">{{ $t('login') }}</div>
            </template>
            <div style="padding:20px;">
              <template v-if="isCnLocale">
                <van-field v-model="loginDialog.loginForm.mobile" autocomplete="tel" :label="$t('mobile')" :placeholder="$t('placeholder_mobile')" />
                <van-field v-model="loginDialog.loginForm.password" type="password" autocomplete="current-password" :label="$t('password')" :placeholder="$t('placeholder_password')" />
              </template>
              <template v-else>
                <van-field v-model="loginDialog.loginForm.email" autocomplete="email" :label="$t('email')" :placeholder="$t('placeholder_email')" />
                <van-field v-model="loginDialog.loginForm.password" type="password" autocomplete="current-password" :label="$t('password')" :placeholder="$t('placeholder_password')" />
              </template>
              <van-button type="primary" class="btn" block style="margin-top:20px;" @click="handleLogin">{{ $t('login') }}</van-button>
              <!-- Google 一键登录（国际版） -->
              <div v-if="showGoogleLogin" style="margin-top: 16px;">
                <div style="display: flex; align-items: center;">
                  <div style="flex: 1; height: 1px; background: #E0DDD8;"></div>
                  <span style="padding: 0 12px; font-size: 12px; color: #8A8A8A;">{{ $t('oauthOr') }}</span>
                  <div style="flex: 1; height: 1px; background: #E0DDD8;"></div>
                </div>
                <div ref="googleBtnContainer" style="margin-top: 12px; display: flex; justify-content: center;"></div>
              </div>
            </div>
          </van-tab>
          <van-tab name="register">
            <template #title>
              <div class="custom-title">{{ $t('register') }}</div>
            </template>
            <div style="padding:20px;">
              <template v-if="isCnLocale">
                <van-field v-model="loginDialog.registerForm.mobile" autocomplete="tel" :label="$t('mobile')" :placeholder="$t('placeholder_mobile')" required />
              </template>
              <template v-else>
                <van-field v-model="loginDialog.registerForm.email" autocomplete="email" :label="$t('email')" :placeholder="$t('placeholder_email')" required />
              </template>
              <van-field v-model="loginDialog.registerForm.code" autocomplete="one-time-code" :label="$t('verification_code')" :placeholder="$t('placeholder_verification_code')">
                <template #button>
                  <van-button size="small" type="primary" plain
                    :loading="loginDialog.sendingCode"
                    :disabled="loginDialog.sendingCode || loginDialog.countdown > 0"
                    :loading-text="$t('sending')"
                    @click="sendVerifyCode('register')">
                    {{ loginDialog.sendCodeBtnText }}
                  </van-button>
                </template>
              </van-field>
              <van-field v-model="loginDialog.registerForm.password" type="password" autocomplete="new-password" :label="$t('password')" :placeholder="$t('placeholder_password')" required />
              <van-field v-model="loginDialog.registerForm.userName" autocomplete="nickname" :label="$t('name')" :placeholder="$t('placeholder_name')" required>
                <template #button>
                  <van-button size="small" type="primary" plain
                    :loading="loginDialog.generatingNickname"
                    :disabled="loginDialog.generatingNickname"
                    @click="generateRandomNickname">{{ $t('random_nickname') }}</van-button>
                </template>
              </van-field>
              <!-- 年龄确认（cn 版备案口径：18 周岁，不对未成年人提供服务——声明见用户协议《未成年人保护》条款） -->
              <div style="padding: 12px 0; text-align: left;">
                <van-checkbox v-model="loginDialog.registerForm.ageConfirmed" shape="square" icon-size="16px">
                  <span style="font-size: 12px; color: #8A8A8A;">{{ isCnRegion ? $t('registerAgeConfirm18') : $t('registerAgeConfirm') }}</span>
                </van-checkbox>
              </div>
              <!-- 条款同意 -->
              <div style="padding: 4px 0 16px 0; text-align: left;">
                <van-checkbox v-model="loginDialog.registerForm.termsAccepted" shape="square" icon-size="16px">
                  <span style="font-size: 12px; color: #8A8A8A;">
                    {{ $t('registerTermsPrefix') }}
                    <a href="#" @click.prevent="showPrivacyPolicy" style="color: #E8543A;">{{ $t('footerPrivacy') }}</a>
                    {{ $t('registerTermsAnd') }}
                    <a href="#" @click.prevent="showTermsOfService" style="color: #E8543A;">{{ $t('footerTerms') }}</a>
                  </span>
                </van-checkbox>
              </div>
              <van-button type="primary" block
                :loading="loginDialog.registering"
                :disabled="loginDialog.registering"
                :loading-text="$t('registering')"
                @click="handleRegister">{{ $t('register') }}</van-button>
            </div>
          </van-tab>
        </van-tabs>
      </van-popup>

    <van-popup v-model="showPrivacyPopup" round position="bottom" :style="{ height: '85%' }">
      <div class="policy-popup">
        <div class="policy-header">
          <span class="policy-title">{{ $t('footerPrivacy') }}</span>
          <van-icon name="cross" size="20" color="#8A8A8A" @click="showPrivacyPopup = false" />
        </div>
        <div class="policy-content" v-if="privacyContent" v-html="privacyContent"></div>
        <div class="policy-loading" v-else>
          <van-loading size="24px">{{ $t('loading') }}</van-loading>
        </div>
      </div>
    </van-popup>

    <!-- 服务条款弹窗 (CN和Global都保留) -->
    <van-popup v-model="showTermsPopup" round position="bottom" :style="{ height: '85%' }">
      <div class="policy-popup">
        <div class="policy-header">
          <span class="policy-title">{{ $t('footerTerms') }}</span>
          <van-icon name="cross" size="20" color="#8A8A8A" @click="showTermsPopup = false" />
        </div>
        <div class="policy-content" v-if="termsContent" v-html="termsContent"></div>
        <div class="policy-loading" v-else>
          <van-loading size="24px">{{ $t('loading') }}</van-loading>
        </div>
      </div>
    </van-popup>

    <!-- Onboarding guide for first-time users -->
    <OnboardingPage />

    <!-- 移动端下载引导条 -->
    <div v-if="isMobileBrowser && !dismissedMobileBanner" class="mobile-download-bar">
      <div class="mobile-download-content">
        <img src="/static/logo-transparent.png" :alt="$t('brand_name')" class="mobile-download-logo">
        <span>{{ $t('mobile_banner_text') }}</span>
      </div>
      <div class="mobile-download-actions">
        <button type="button" class="mobile-download-btn" @click="handleMobileDownloadClick">{{ $t('mobile_banner_btn') }}</button>
        <van-icon name="cross" size="16" color="#999" @click="dismissedMobileBanner = true" />
      </div>
    </div>

    <!-- 微信/小红书等内置浏览器引导遮罩 -->
    <div v-if="showBrowserGuide" class="browser-guide-overlay" @click="showBrowserGuide = false">
      <div class="browser-guide-arrow">
        <svg width="60" height="80" viewBox="0 0 60 80" fill="none">
          <path d="M30 70 L30 20 M30 20 L15 35 M30 20 L45 35" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="browser-guide-text">
        <p style="font-size: 18px; font-weight: 600; margin-bottom: 8px;">{{ guideTitle }}</p>
        <p style="font-size: 14px; line-height: 1.6; opacity: 0.9;">{{ guideText }}</p>
      </div>
    </div>

    <!-- 桌面端布局 -->
    <div class="app-layout" :class="{ 'mobile-with-bottom-bar': isMobileBrowser && !dismissedMobileBanner, 'mobile-with-tabbar': isMobileBrowser }">
      <SidebarNav v-if="!isMobileBrowser" />
      <div class="main-area" :class="{ 'with-sidebar': !isMobileBrowser }">
        <Header :is-mobile-browser="isMobileBrowser" @toggle-mobile-sidebar="showMobileSidebar = !showMobileSidebar" />
        <div class="page-container">
          <router-view />
        </div>
        <Footer />
      </div>
      <!-- 移动端底部导航（中间凸起「+」发起 AI 需求澄清，对齐 client/ custom_bottom_nav_bar） -->
      <BottomNavBar v-if="isMobileBrowser" :banner-visible="isMobileBrowser && !dismissedMobileBanner" />
    </div>

    <!-- 全局独立 AI 对话弹窗（首页「发布消息」/底部「+」/Onboarding/草稿续写的统一入口，替代原 /ai 独立页） -->
    <AiChatPopup />

    <!-- 移动端左侧抽屉菜单 -->
    <div v-if="isMobileBrowser && showMobileSidebar" class="mobile-sidebar-overlay" @click.self="showMobileSidebar = false">
      <SidebarNav class="mobile-sidebar" />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Vue from 'vue'
import CryptoJS from 'crypto-js'
import { Dialog, Field, Button, Popup, Toast, Popover, Tabs, Tab, Checkbox } from 'vant'
Vue.use(Dialog)
Vue.use(Field)
Vue.use(Button)
Vue.use(Toast)
Vue.use(Popup)
Vue.use(Popover)
Vue.use(Tabs)
Vue.use(Tab)
Vue.use(Checkbox)

import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import Header from '@/components/Header.vue'
import AiChatPopup from '@/components/AiChatPopup.vue'
import Footer from '@/components/Footer.vue'
import OnboardingPage from '@/components/OnboardingPage.vue'
import SidebarNav from '@/components/SidebarNav.vue'
import BottomNavBar from '@/components/BottomNavBar.vue'
import wsService from '@/services/websocket.js'
import { loadPolicyDoc } from '@/i18n/policies/loader.js'
import { isMobileBrowser } from '@/utils/mobile-detect.js'

export default {
  name: 'App',
  components: {
    LanguageSwitcher,
    Header,
    Footer,
    OnboardingPage,
    SidebarNav,
    BottomNavBar,
    AiChatPopup
  },
  data() {
    return {
      currentDomain: '',
      mountedExecuted: false,
      wsInitialized: false, // 防止 WebSocket 重复初始化
      unreadDebounceTimer: null, // WS 未读事件防抖定时器
      // 隐私政策/服务条款弹窗
      showPrivacyPopup: false,
      showTermsPopup: false,
      privacyContent: '',
      termsContent: '',
      privacyContentLocale: '',
      termsContentLocale: '',
      loginDialog: {
        activeTab: 'login',
        sendCodeBtnText: this.$t('send_code'),
        countdown: 0,
        sendingCode: false,
        generatingNickname: false,
        registering: false,
        loginForm: {
          email: '',
          password: '',
          mobile: '',
          smsCode: ''
        },
        registerForm: {
          email: '',
          mobile: '',
          userName: '',
          code: '',
          password: '',
          ageConfirmed: false,
          termsAccepted: false
        }
      },
      isMobileBrowser: false,
      dismissedMobileBanner: false,
      showBrowserGuide: false,
      showMobileSidebar: false
    }
  },
  computed: {
    beforeEatchInitialized() {
      return this.$store.state.beforeEatchInitialized;
    },
    wsRegistered() {
      return this.$store.state.wsRegistered
    },
    alreadyLogin() {
      return this.$store.state.alreadyLogin;
    },
    isCnLocale() {
      return this.$i18n.locale === 'zh'
    },
    isCnRegion() {
      return this.$config && this.$config.region === 'cn'
    },
    showGoogleLogin() {
      // 国际版且配置了 Google Web Client ID 时展示
      return !this.isCnRegion && !!(this.$config && this.$config.googleWebClientId)
    },
    // 内置浏览器检测
    isWeChat() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('micromessenger') !== -1
    },
    isXiaohongshu() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('xhsdiscover') !== -1 || ua.indexOf('xiaohongshu') !== -1
    },
    isQQ() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('qq/') !== -1 && ua.indexOf('mqqbrowser') === -1
    },
    isWeibo() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('weibo') !== -1
    },
    isInAppBrowser() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return this.isWeChat || this.isXiaohongshu || this.isQQ || this.isWeibo ||
        ua.indexOf('fbav') !== -1 ||
        ua.indexOf('instagram') !== -1 ||
        ua.indexOf('reddit') !== -1 ||
        ua.indexOf('snssdk') !== -1 ||
        ua.indexOf('bytedance') !== -1 ||
        ua.indexOf('linkedinapp') !== -1 ||
        ua.indexOf('line/') !== -1
    },
    isIOS() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return (ua.indexOf('iphone') !== -1 || ua.indexOf('ipad') !== -1 || ua.indexOf('ipod') !== -1) && !window.MSStream
    },
    isAndroid() {
      var ua = (navigator.userAgent || '').toLowerCase()
      return ua.indexOf('android') !== -1 || ua.indexOf('adr') !== -1
    },
    appStoreUrl() {
      return this.isCnRegion
        ? 'https://apps.apple.com/cn/app/章摇摇/id6760103197'
        : 'https://apps.apple.com/us/app/octopie-ai/id6760101997'
    },
    googlePlayUrl() {
      return 'https://play.google.com/store/apps/details?id=ai.octopie.client'
    },
    apkUrl() {
      return this.isCnRegion
        ? 'https://download.octopie.cn/octopie_cn.apk'
        : 'https://download.octosay.ai/octopie_ai.apk'
    },
    // 根据 UA 决定下载链接
    downloadUrl() {
      if (this.isIOS) {
        return this.appStoreUrl
      }
      if (this.isAndroid) {
        if (this.isCnRegion) {
          return this.apkUrl
        }
        return this.googlePlayUrl
      }
      // UA 无法识别时：CN 默认 Android APK，Global 默认 App Store
      if (this.isCnRegion) {
        return this.apkUrl
      }
      return this.appStoreUrl
    },
    guideTitle() {
      if (this.isWeChat) {
        return this.isCnRegion ? '请在浏览器中打开' : 'Open in Browser'
      }
      return this.isCnRegion ? '请在系统浏览器中打开' : 'Open in System Browser'
    },
    guideText() {
      if (this.isCnRegion) {
        return '点击右上角 "..." 或 "···" 按钮，选择"在浏览器中打开"，即可下载 App'
      }
      return 'Tap the "..." or "···" button in the top right, then select "Open in Browser" to download the app'
    }
  },
  watch: {
    beforeEatchInitialized(newValue, oldValue) {
      if (newValue) {
        this.doMounted(); // 箭头函数自动绑定外层 this
      }
    },
    wsRegistered(newVal) {
      if (newVal) {
        // WS 注册成功后，检查未读消息
        this.checkUnreadContacts()
        this.checkUnreadMatch()
        this.checkUnreadChannelMsg()
      }
    },
    alreadyLogin(newVal, oldVal) {
      // 注意：oldVal 第一次可能是 undefined，不能用来判断
      // 使用 wsInitialized 标志位来防止重复初始化
      if (newVal) {
        this.initWebSocket()
        // 登录后立即检查未读消息（不等 WS 注册）
        this.checkUnreadContacts()
        this.checkUnreadMatch()
        this.checkUnreadChannelMsg()
      } else if (oldVal) {
        // 从登录变为登出
        this.disconnectWebSocket()
      }
    },
    $route(to) {
      // 移动端切换路由后自动关闭左侧抽屉菜单
      this.showMobileSidebar = false
      // 离开聊天相关页面时，清除当前聊天对象状态
      if (to.path.indexOf('/messages') == -1 && to.path.indexOf('/matched-session') == -1) {
        this.$store.commit('setCurrentChatWithUserId', null)
      }
    }
  },
  async mounted() {

    this.currentDomain = window.location.hostname
    this.isMobileBrowser = isMobileBrowser()

    // 注意，这个函数和index.js的router.beforeEach关系不大，即便在router.beforeEach中直接return了，不next()，此函数也会执行，且执行顺序也没依赖关系！所以如果有数据依赖，采用computed+watch来解决
    // 然后严禁在这里直接访问需要带token的请求，因为很可能还没来得及在router.beforeEach设置
    // 所以checkHaveUnreadChatMsg里面，得判断是否有token，没有的话就别发送，否则可能登录失败，然后反过来把后来设置的token又干掉了
    // 但是各个子组件的mounted()是一定会在router.beforeEach后执行！
    // 如果有全局需要初始化的操作，可以在main.js中去补充到globalInit

    if (this.$store.state.beforeEatchInitialized) {
      this.doMounted()
    }
  },
  methods: {
    // 初始化全局 WebSocket
    initWebSocket() {
      // 使用标志位防止重复初始化
      if (this.wsInitialized) {
        console.log('[App] WebSocket already initialized, skip')
        return
      }
      
      const token = this.$common.getToken()
      if (!token) {
        console.log('[App] No token, skip WebSocket init')
        return
      }
      
      this.wsInitialized = true
      console.log('[App] Initializing global WebSocket')
      wsService.connect(this.$config.chatHost, token)
      
      // 使用命名监听器，防止重复注册
      // 监听 WebSocket 状态变化
      wsService.on('registered', (msg) => {
        this.$store.state.wsConnected = true
        this.$store.state.wsRegistered = true
        this.$store.state.wsUserId = msg.userId
      }, 'app_registered')
      
      wsService.on('disconnected', () => {
        this.$store.state.wsConnected = false
        this.$store.state.wsRegistered = false
      }, 'app_disconnected')
      
      wsService.on('error', () => {
        this.$store.state.wsConnected = false
        this.$store.state.wsRegistered = false
      }, 'app_error')

      // 监听新消息和互动通知，实时更新未读红点
      wsService.on('msg', (msg) => {
        this.onWebSocketMsg(msg)
      }, 'app_msg')

      wsService.on('interaction_notification', () => {
        this.onInteractionNotification()
      }, 'app_interaction')

      // 匹配结果通知：先乐观点亮 Matches 红点，随后拉取真实未读数（数字与角标一并刷新）
      wsService.on('match_found', () => {
        this.$store.commit('setHasUnreadMatch', true)
        this.checkUnreadMatch()
      }, 'app_match_found')
    },
    
    disconnectWebSocket() {
      console.log('[App] Disconnecting global WebSocket')
      this.wsInitialized = false
      wsService.disconnect()
      this.$store.state.wsConnected = false
      this.$store.state.wsRegistered = false
      this.$store.state.wsUserId = null
    },
    
    // doMounted一定是在beforeEach执行后才被执行
    async doMounted() {
      if (this.mountedExecuted) {
        return
      }
      this.mountedExecuted = true

      // Check if first-time user needs onboarding
      this.checkOnboarding()

      // 初始化全局 WebSocket
      if (this.$store.state.alreadyLogin) {
        this.initWebSocket()
        // 检查未读消息
        this.checkUnreadContacts()
        try { this.checkUnreadMatch() } catch(e) { console.error('checkUnreadMatch error:', e) }
        this.checkUnreadChannelMsg()
      }

    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    generateRandomNickname() {
      const vm = this
      if (vm.loginDialog.generatingNickname) return
      vm.loginDialog.generatingNickname = true
      axios.post('/api/user/generate_nickname', {}).then(
        response => {
          vm.loginDialog.generatingNickname = false
          const body = response.data
          if (body.code !== 0 || !body.data || !body.data.nickname) {
            Toast(body.data || vm.$t('operationFailed'))
            return
          }
          vm.loginDialog.registerForm.userName = body.data.nickname
        }
      ).catch(error => {
        vm.loginDialog.generatingNickname = false
        console.error('Failed to generate nickname:', error)
        Toast(vm.$t('networkError'))
      })
    },
    sendVerifyCode(type) {      let vm = this
      if (vm.isCnLocale) {
        const mobile = type === 'login' ? vm.loginDialog.loginForm.mobile : vm.loginDialog.registerForm.mobile
        if (!mobile) {
          Toast(vm.$t('toast_enter_mobile'))
          return
        }
        if (!mobile.startsWith('1') || mobile.length != 11) {
          Toast(vm.$t('toast_invalid_mobile'))
          return
        }
        vm.loginDialog.sendingCode = true
        const url = '/api/user/send_sms_code'
        const req = { mobile: mobile }
        axios.post(url, req).then(
          response => {
            vm.loginDialog.sendingCode = false
            const body = response.data
            if (body.code !== 0) {
              Toast(body.data || vm.$t('toast_send_code_failed'))
              return
            }
            Toast(vm.$t('toast_code_sent_sms'))
            vm.loginDialog.countdown = 60
            vm.loginDialog.sendCodeBtnText = vm.$t('retry_in_seconds', {countdown: vm.loginDialog.countdown})
            const timer = setInterval(() => {
              vm.loginDialog.countdown--
              if (vm.loginDialog.countdown <= 0) {
                clearInterval(timer)
                vm.loginDialog.sendCodeBtnText = vm.$t('send_code')
              } else {
                vm.loginDialog.sendCodeBtnText = vm.$t('retry_in_seconds', {countdown: vm.loginDialog.countdown})
              }
            }, 1000)
          }
        ).catch(error => {
          vm.loginDialog.sendingCode = false
          console.error('Failed to send verification code:', error)
          Toast(vm.$t('toast_send_code_error'))
        })
        return
      }

      if (type === 'login') {
        return
      }
      const email = vm.loginDialog.registerForm.email
      if (!email) {
        Toast(vm.$t('toast_enter_email'))
        return
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(email)) {
        Toast(vm.$t('toast_invalid_email'))
        return
      }
      vm.loginDialog.sendingCode = true
      const url = '/api/user/send_verify_code'
      const req = { email: email }
      axios.post(url, req).then(
        response => {
          vm.loginDialog.sendingCode = false
          const body = response.data
          if (body.code !== 0) {
            Toast(body.data || vm.$t('toast_send_code_failed'))
            return
          }
          Toast(vm.$t('toast_code_sent'))
          vm.loginDialog.countdown = 60
          vm.loginDialog.sendCodeBtnText = vm.$t('retry_in_seconds', {countdown: vm.loginDialog.countdown})
          const timer = setInterval(() => {
            vm.loginDialog.countdown--
            if (vm.loginDialog.countdown <= 0) {
              clearInterval(timer)
              vm.loginDialog.sendCodeBtnText = vm.$t('send_code')
            } else {
              vm.loginDialog.sendCodeBtnText = vm.$t('retry_in_seconds', {countdown: vm.loginDialog.countdown})
            }
          }, 1000)
        }
      ).catch(error => {
        vm.loginDialog.sendingCode = false
        console.error('Failed to send verification code:', error)
        Toast(vm.$t('toast_send_code_error'))
      })
    },
    handleLogin() {
      let vm = this
      if (vm.isCnLocale) {
        const { mobile, password } = vm.loginDialog.loginForm
        if (!mobile || !password) {
          Toast(vm.$t('toast_fill_all_fields'))
          return
        }
        const url = '/api/user/login_by_mobile'
        const hashedPassword = CryptoJS.SHA256(password).toString()
        const req = { mobile: mobile, passwd: hashedPassword }
        axios.post(url, req).then(
          response => {
            const body = response.data
            if (body.code !== 0) {
              Toast(body.data || vm.$t('toast_login_failed'))
              return
            }
            vm.handleLoginSuccess(body.data)
            Toast(vm.$t('toast_login_success'))
          }
        ).catch(error => {
          console.error('Login failed:', error)
          Toast(vm.$t('toast_login_error'))
        })
        return
      }

      const { email, password } = vm.loginDialog.loginForm
      if (!email || !password) {
        Toast(vm.$t('toast_fill_all_fields'))
        return
      }
      const url = '/api/user/login'
      const hashedPassword = CryptoJS.SHA256(password).toString()
      const req = { email: email, passwd: hashedPassword }
      axios.post(url, req).then(
        response => {
          const body = response.data
          if (body.code !== 0) {
            Toast(body.data || vm.$t('toast_login_failed'))
            return
          }
          vm.handleLoginSuccess(body.data)
          Toast(vm.$t('toast_login_success'))
        }
      ).catch(error => {
        console.error('Login failed:', error)
        Toast(vm.$t('toast_login_error'))
      })
    },
    handleRegister() {
      let vm = this
      if (vm.isCnLocale) {
        const { mobile, userName, password, code, ageConfirmed, termsAccepted } = vm.loginDialog.registerForm
        if (!mobile || !userName || !password || !code) {
          Toast(vm.$t('toast_fill_all_fields'))
          return
        }
        if (!ageConfirmed) {
          Toast(vm.$t('registerAgeError'))
          return
        }
        if (!termsAccepted) {
          Toast(vm.$t('registerTermsError'))
          return
        }
        vm.loginDialog.registering = true
        const url = '/api/user/register_by_mobile'
        const hashedPassword = CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(password)).toString()
        const req = { mobile: mobile, userName: userName, passwd: hashedPassword, smsCode: code, userId: vm.$common.getUserId() }
        axios.post(url, req).then(
          response => {
            vm.loginDialog.registering = false
            const body = response.data
            if (body.code !== 0) {
              Toast(body.data || vm.$t('toast_register_failed'))
              return
            }
            vm.handleLoginSuccess(body.data)
            Toast(vm.$t('toast_register_success'))
          }
        ).catch(error => {
          vm.loginDialog.registering = false
          console.error('Registration failed:', error)
          Toast(vm.$t('toast_register_error'))
        })
        return
      }

      const { email, userName, password, code, ageConfirmed, termsAccepted } = vm.loginDialog.registerForm
      if (!email || !userName || !password || !code) {
        Toast(vm.$t('toast_fill_all_fields'))
        return
      }
      if (!ageConfirmed) {
        Toast(vm.$t('registerAgeError'))
        return
      }
      if (!termsAccepted) {
        Toast(vm.$t('registerTermsError'))
        return
      }
      vm.loginDialog.registering = true
      const url = '/api/user/register'
      const hashedPassword = CryptoJS.SHA256(CryptoJS.enc.Utf8.parse(password)).toString()
      const req = { email: email, userName: userName, passwd: hashedPassword, verifyCode: code, userId: vm.$common.getUserId() }
      axios.post(url, req).then(
        response => {
          vm.loginDialog.registering = false
          const body = response.data
          if (body.code !== 0) {
            Toast(body.data || vm.$t('toast_register_failed'))
            return
          }
          vm.handleLoginSuccess(body.data)
          Toast(vm.$t('toast_register_success'))
        }
      ).catch(error => {
        vm.loginDialog.registering = false
        console.error('Registration failed:', error)
        Toast(vm.$t('toast_register_error'))
      })
    },
    handleLoginSuccess(data) {
      this.$common.handleLoginSuccess(data)
    },
    // ==================== Google 一键登录 ====================
    // GSI 脚本改为按需动态加载：仅在非 cn region 打开登录弹窗时注入，
    // 避免 cn 版加载被墙的 accounts.google.com 脚本拖慢页面
    loadGoogleGsiScript() {
      if (document.getElementById('google-gsi-script')) return
      const script = document.createElement('script')
      script.id = 'google-gsi-script'
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      document.head.appendChild(script)
    },
    onLoginPopupOpened() {
      if (!this.showGoogleLogin) return
      this.loadGoogleGsiScript()
      this.$nextTick(() => {
        this.renderGoogleButton(0)
      })
    },
    renderGoogleButton(retryCount) {
      if (!this.showGoogleLogin) return
      const el = this.$refs.googleBtnContainer
      if (!el) return
      // GSI 脚本是打开弹窗时才动态加载的，可能还没就绪，最多重试 20 次（6 秒）
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        if (retryCount < 20) {
          setTimeout(() => {
            this.renderGoogleButton(retryCount + 1)
          }, 300)
        }
        return
      }
      // 已经渲染过就不再重复渲染
      if (el.childElementCount > 0) return
      window.google.accounts.id.initialize({
        client_id: this.$config.googleWebClientId,
        callback: (response) => {
          this.handleGoogleCredential(response.credential)
        }
      })
      const width = Math.min(320, el.clientWidth || 320)
      window.google.accounts.id.renderButton(el, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        text: 'continue_with',
        width: width
      })
    },
    handleGoogleCredential(credential) {
      // 客户端拦截器会自动带上当前匿名用户的 token，后端做匿名转正
      const url = '/api/user/login_by_google'
      axios.post(url, { idToken: credential, clientType: 'browser' }).then(
        response => {
          const body = response.data
          if (body.code !== 0) {
            Toast(body.data || this.$t('toast_oauth_login_failed'))
            return
          }
          this.handleLoginSuccess(body.data)
          Toast(this.$t('toast_login_success'))
        }
      ).catch(error => {
        console.error('Google login failed:', error)
        Toast(this.$t('toast_oauth_login_error'))
      })
    },
    async showPrivacyPolicy() {
      this.showPrivacyPopup = true
      // 政策文档按语言缓存：global 版支持切换语言，切语言后需加载对应语言文档
      const locale = this.$i18n.locale
      if (!this.privacyContent || this.privacyContentLocale !== locale) {
        this.privacyContent = await loadPolicyDoc(this, 'privacy')
        this.privacyContentLocale = locale
      }
    },
    async showTermsOfService() {
      this.showTermsPopup = true
      const locale = this.$i18n.locale
      if (!this.termsContent || this.termsContentLocale !== locale) {
        this.termsContent = await loadPolicyDoc(this, 'terms')
        this.termsContentLocale = locale
      }
    },
    checkOnboarding() {
      var completed = window.localStorage.getItem('hasCompletedOnboarding')
      if (!completed) {
        this.$store.commit('setShowOnboarding', true)
      }
    },
    handleMobileDownloadClick() {
      // 微信/小红书：弹出引导遮罩，不直接跳转
      if (this.isWeChat || this.isXiaohongshu) {
        this.showBrowserGuide = true
        return
      }
      // 其他 in-app browser 和普通浏览器：统一用 window.location.href 跳转，避免 <a> 默认行为带回首页
      window.location.href = this.downloadUrl
    },

    // ==================== 全局未读消息检查（从 FrontPage 迁移） ====================

    async checkUnreadContacts() {
      try {
        var response = await axios.post('/api/chat/check_have_unread_msg', {})
        var body = response.data
        if (body.code == 0 && body.data) {
          var has = body.data.haveUnreadMsg == 1
          this.$store.commit('setUnreadChatCount', body.data.unreadChatCount)
          this.$store.commit('setUnreadInteractionCount', body.data.unreadInteractionCount)
          this.$store.commit('setHasUnreadChatMsg', has)
          this.$store.commit('setHasUnreadMsg', has)
        }
      } catch (e) {
        console.warn('[App] checkUnreadContacts failed', e)
      }
    },

    async checkUnreadMatch() {
      try {
        var response = await axios.post('/api/chat/check_have_unread_match', {})
        var body = response.data
        if (body.code == 0 && body.data) {
          this.$store.commit('setHasUnreadMatch', body.data.haveUnreadMatch == 1)
          this.$store.commit('setUnreadMatchCount', body.data.unreadMatchCount)
        }
      } catch (e) {
        console.warn('[App] checkUnreadMatch failed', e)
      }
    },

    async checkUnreadChannelMsg() {
      try {
        var response = await axios.post('/api/chat/check_have_unread_channel_msg', {})
        var body = response.data
        if (body.code == 0 && body.data) {
          this.$store.commit('setHasUnreadChannelMsg', body.data.haveUnreadChannelMsg == 1)
        }
      } catch (e) {
        console.warn('[App] checkUnreadChannelMsg failed', e)
      }
    },

    // WS 消息到达时实时更新未读红点
    onWebSocketMsg(msg) {
      var vue = this
      vue.$nextTick(function() {
        // 如果当前正在与该用户聊天，不标为未读（ChatBox 已处理 read 状态）
        var currentChatWithUserId = vue.$store.state.currentChatWithUserId
        if (currentChatWithUserId && msg.fromUserId == currentChatWithUserId) {
          return
        }
        if (msg.read != '1') {
          // 免打扰发送方：不点亮红点/按钮（其未读不计入总数，行内自有红点），
          // 避免点亮后被防抖刷新纠回造成的按钮闪现
          const isDnd = vue.$store.state.contactDnd[msg.fromUserId] == true
          if (!isDnd) {
            vue.$store.commit('setHasUnreadChatMsg', true)
            vue.$store.commit('setHasUnreadMsg', true)
            // 数字不做本地累加（会与"点击菜单临时清零"冲突，出现错误小数字），
            // 防抖后拉取后端真实未读数
            vue.scheduleUnreadRefresh()
          }
        }
      })
    },

    // WS 互动通知到达时实时更新未读红点
    onInteractionNotification() {
      this.$store.commit('setHasUnreadInteraction', true)
      this.$store.commit('setHasUnreadMsg', true)
      this.scheduleUnreadRefresh()
    },

    // WS 未读事件触发的防抖刷新：数字始终以后端真实值为准
    scheduleUnreadRefresh() {
      if (this.unreadDebounceTimer) {
        clearTimeout(this.unreadDebounceTimer)
      }
      this.unreadDebounceTimer = setTimeout(() => {
        this.checkUnreadContacts()
      }, 500)
    }
  }
}
</script>

<style scoped>
.small-btn .van-button {
  height: 24px;
}

.small-btn .van-button--normal {
  font-size: 0.75rem;
}

.policy-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.policy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #E0DDD8;
  flex-shrink: 0;
}

.policy-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1A1A;
}

.policy-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  font-size: 13px;
  line-height: 1.8;
  color: #4A4A4A;
  text-align: left;
}

.policy-content ::v-deep h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 20px 0 10px 0;
  color: #1A1A1A;
}

.policy-content ::v-deep h3 {
  font-size: 14px;
  font-weight: 500;
  margin: 16px 0 8px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep p {
  margin: 8px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep ul {
  margin: 8px 0;
  padding-left: 20px;
}

.policy-content ::v-deep li {
  margin: 4px 0;
  color: #4A4A4A;
}

.policy-content ::v-deep a {
  color: #E8543A;
}

.policy-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>




// WEBPACK FOOTER //
// src/App.vue